import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import PageHeader from '../components/PageHeader';
import {
  EmbeddingsConfigView,
  MilvusConfigView,
  OcrConfigView,
  systemApi,
  WeChatMpConfigView,
} from '../services/api';

type Status = 'on' | 'off' | 'partial';

function statusChip(status: Status) {
  if (status === 'on') return <Chip size="small" color="success" label="已就绪" />;
  if (status === 'partial') return <Chip size="small" color="warning" label="未就绪" />;
  return <Chip size="small" color="default" label="未启用" />;
}

const gridSx = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  mb: 2,
} as const;

const SystemConfig: React.FC = () => {
  const [ocr, setOcr] = useState<OcrConfigView | null>(null);
  const [emb, setEmb] = useState<EmbeddingsConfigView | null>(null);
  const [milvus, setMilvus] = useState<MilvusConfigView | null>(null);
  const [wechat, setWechat] = useState<WeChatMpConfigView | null>(null);

  const [ocrEnabled, setOcrEnabled] = useState(false);
  const [ocrProvider, setOcrProvider] = useState('custom');
  const [ocrUrl, setOcrUrl] = useState('');
  const [ocrKey, setOcrKey] = useState('');
  const [ocrSecret, setOcrSecret] = useState('');

  const [embEnabled, setEmbEnabled] = useState(false);
  const [embUrl, setEmbUrl] = useState('');
  const [embModel, setEmbModel] = useState('text-embedding-3-small');
  const [embKey, setEmbKey] = useState('');

  const [milvusEnabled, setMilvusEnabled] = useState(false);
  const [milvusHost, setMilvusHost] = useState('127.0.0.1');
  const [milvusPort, setMilvusPort] = useState(19530);
  const [milvusCollection, setMilvusCollection] = useState('knowledge_entries');

  const [wxEnabled, setWxEnabled] = useState(false);
  const [wxAppId, setWxAppId] = useState('');
  const [wxSecret, setWxSecret] = useState('');
  const [wxCover, setWxCover] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [o, e, m, w] = await Promise.all([
        systemApi.getOcrConfig(),
        systemApi.getEmbeddingsConfig(),
        systemApi.getMilvusConfig(),
        systemApi.getWechatConfig(),
      ]);
      setOcr(o);
      setOcrEnabled(o.enabled);
      setOcrProvider(o.provider || 'custom');
      setOcrUrl(o.apiUrl || '');
      setOcrKey('');
      setOcrSecret('');

      setEmb(e);
      setEmbEnabled(e.enabled);
      setEmbUrl(e.apiUrl || '');
      setEmbModel(e.model || 'text-embedding-3-small');
      setEmbKey('');

      setMilvus(m);
      setMilvusEnabled(m.enabled);
      setMilvusHost(m.host || '127.0.0.1');
      setMilvusPort(m.port || 19530);
      setMilvusCollection(m.collection || 'knowledge_entries');

      setWechat(w);
      setWxEnabled(w.enabled);
      setWxAppId(w.appId || '');
      setWxSecret('');
      setWxCover(w.defaultCoverMediaId || '');
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || '加载系统配置失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runSave = async (key: string, fn: () => Promise<void>) => {
    setSaving(key);
    setError('');
    setSuccess('');
    try {
      await fn();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || '保存失败');
    } finally {
      setSaving(null);
    }
  };

  return (
    <Box>
      <PageHeader
        title="系统配置"
        subtitle="OCR / 向量 Embeddings / Milvus / 微信均可在此动态配置，保存后立即生效（无需重启）"
        breadcrumbs={[{ label: '系统管理' }, { label: '系统配置' }]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && ocr && emb && milvus && wechat && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* OCR */}
          <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ fontSize: '1.0625rem', fontWeight: 700 }}>
                OCR 图片识别
              </Typography>
              {statusChip(!ocr.enabled ? 'off' : ocr.configured ? 'on' : 'partial')}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.65 }}>
              开启后，上传 PNG/JPG 可提取文字入库；关闭则跳过图片正文，避免乱码。
            </Typography>
            <FormControlLabel
              control={<Switch checked={ocrEnabled} onChange={(e) => setOcrEnabled(e.target.checked)} />}
              label={ocrEnabled ? '已开启' : '已关闭'}
              sx={{ mb: 2, display: 'block' }}
            />
            <Box sx={gridSx}>
              <TextField select label="Provider" size="small" value={ocrProvider} onChange={(e) => setOcrProvider(e.target.value)} fullWidth>
                <MenuItem value="custom">custom</MenuItem>
                <MenuItem value="baidu">baidu</MenuItem>
                <MenuItem value="tencent">tencent</MenuItem>
              </TextField>
              <TextField label="接口地址" size="small" fullWidth value={ocrUrl} onChange={(e) => setOcrUrl(e.target.value)} />
              <TextField
                label="接口密钥"
                size="small"
                type="password"
                fullWidth
                value={ocrKey}
                onChange={(e) => setOcrKey(e.target.value)}
                helperText={ocr.apiKeySet ? `已配置（${ocr.apiKeyMasked}），留空不改` : '尚未配置'}
              />
              <TextField
                label="Secret（可选）"
                size="small"
                type="password"
                fullWidth
                value={ocrSecret}
                onChange={(e) => setOcrSecret(e.target.value)}
                helperText={ocr.apiSecretSet ? '已配置，留空不改' : '部分厂商需要'}
              />
            </Box>
            <Button
              variant="contained"
              disabled={saving === 'ocr'}
              onClick={() =>
                runSave('ocr', async () => {
                  const updated = await systemApi.updateOcrConfig({
                    enabled: ocrEnabled,
                    provider: ocrProvider,
                    apiUrl: ocrUrl,
                    ...(ocrKey.trim() ? { apiKey: ocrKey.trim() } : {}),
                    ...(ocrSecret.trim() ? { apiSecret: ocrSecret.trim() } : {}),
                  });
                  setOcr(updated);
                  setOcrKey('');
                  setOcrSecret('');
                  setSuccess(updated.ready ? 'OCR 已就绪' : 'OCR 已保存');
                })
              }
            >
              {saving === 'ocr' ? <CircularProgress size={20} color="inherit" /> : '保存 OCR'}
            </Button>
          </Paper>

          {/* Embeddings */}
          <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ fontSize: '1.0625rem', fontWeight: 700 }}>
                向量 Embeddings
              </Typography>
              {statusChip(!emb.enabled ? 'off' : emb.ready ? 'on' : 'partial')}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.65 }}>
              <strong>好处：</strong>语义搜索能按「意思」召回文档（同义/换说法也能搜到），问答检索更准；关闭则退回关键词 LIKE，省费用但召回弱。
            </Typography>
            <FormControlLabel
              control={<Switch checked={embEnabled} onChange={(e) => setEmbEnabled(e.target.checked)} />}
              label={embEnabled ? '已开启' : '已关闭'}
              sx={{ mb: 2, display: 'block' }}
            />
            <Box sx={gridSx}>
              <TextField label="Embeddings API URL" size="small" fullWidth value={embUrl} onChange={(e) => setEmbUrl(e.target.value)} />
              <TextField label="模型" size="small" fullWidth value={embModel} onChange={(e) => setEmbModel(e.target.value)} />
              <TextField
                label="API Key"
                size="small"
                type="password"
                fullWidth
                value={embKey}
                onChange={(e) => setEmbKey(e.target.value)}
                helperText={emb.apiKeySet ? `已配置（${emb.apiKeyMasked}），留空不改` : 'OpenAI 兼容密钥'}
                sx={{ gridColumn: { sm: '1 / -1' } }}
              />
            </Box>
            <Button
              variant="contained"
              disabled={saving === 'emb'}
              onClick={() =>
                runSave('emb', async () => {
                  const updated = await systemApi.updateEmbeddingsConfig({
                    enabled: embEnabled,
                    apiUrl: embUrl,
                    model: embModel,
                    ...(embKey.trim() ? { apiKey: embKey.trim() } : {}),
                  });
                  setEmb(updated);
                  setEmbKey('');
                  setSuccess(updated.ready ? 'Embeddings 已就绪' : 'Embeddings 已保存');
                })
              }
            >
              {saving === 'emb' ? <CircularProgress size={20} color="inherit" /> : '保存 Embeddings'}
            </Button>
          </Paper>

          {/* Milvus */}
          <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ fontSize: '1.0625rem', fontWeight: 700 }}>
                Milvus 向量库
              </Typography>
              {statusChip(!milvus.enabled ? 'off' : milvus.ready ? 'on' : 'partial')}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.65 }}>
              <strong>好处：</strong>知识量大时 ANN 检索更快更稳；未开则用部门范围内存余弦检索（小库够用）。依赖 Embeddings 已生成向量。保存后会自动重连。
            </Typography>
            <FormControlLabel
              control={<Switch checked={milvusEnabled} onChange={(e) => setMilvusEnabled(e.target.checked)} />}
              label={milvusEnabled ? '已开启' : '已关闭'}
              sx={{ mb: 2, display: 'block' }}
            />
            <Box sx={gridSx}>
              <TextField label="Host" size="small" fullWidth value={milvusHost} onChange={(e) => setMilvusHost(e.target.value)} />
              <TextField
                label="Port"
                size="small"
                type="number"
                fullWidth
                value={milvusPort}
                onChange={(e) => setMilvusPort(Number(e.target.value) || 19530)}
              />
              <TextField
                label="Collection"
                size="small"
                fullWidth
                value={milvusCollection}
                onChange={(e) => setMilvusCollection(e.target.value)}
                sx={{ gridColumn: { sm: '1 / -1' } }}
              />
            </Box>
            <Button
              variant="contained"
              disabled={saving === 'milvus'}
              onClick={() =>
                runSave('milvus', async () => {
                  const updated = await systemApi.updateMilvusConfig({
                    enabled: milvusEnabled,
                    host: milvusHost,
                    port: milvusPort,
                    collection: milvusCollection,
                  });
                  setMilvus(updated);
                  setSuccess(updated.ready ? 'Milvus 已连接' : updated.enabled ? '已开启但未连上，已回退内存检索' : 'Milvus 已关闭');
                })
              }
            >
              {saving === 'milvus' ? <CircularProgress size={20} color="inherit" /> : '保存 Milvus'}
            </Button>
          </Paper>

          {/* WeChat */}
          <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ fontSize: '1.0625rem', fontWeight: 700 }}>
                微信公众号草稿同步
              </Typography>
              {statusChip(!wechat.enabled ? 'off' : wechat.ready ? 'on' : 'partial')}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.65 }}>
              <strong>好处：</strong>公众号文章页可一键写入微信草稿箱，在 mp.weixin.qq.com 审校发布；关闭则只能本地生成稿件。需公众平台 IP 白名单。
            </Typography>
            <FormControlLabel
              control={<Switch checked={wxEnabled} onChange={(e) => setWxEnabled(e.target.checked)} />}
              label={wxEnabled ? '已开启' : '已关闭'}
              sx={{ mb: 2, display: 'block' }}
            />
            <Box sx={gridSx}>
              <TextField label="AppId" size="small" fullWidth value={wxAppId} onChange={(e) => setWxAppId(e.target.value)} />
              <TextField
                label="AppSecret"
                size="small"
                type="password"
                fullWidth
                value={wxSecret}
                onChange={(e) => setWxSecret(e.target.value)}
                helperText={wechat.appSecretSet ? `已配置（${wechat.appSecretMasked}），留空不改` : '尚未配置'}
              />
              <TextField
                label="默认封面 media_id（可选）"
                size="small"
                fullWidth
                value={wxCover}
                onChange={(e) => setWxCover(e.target.value)}
                sx={{ gridColumn: { sm: '1 / -1' } }}
                helperText="未上传封面时使用；来自微信永久素材 thumb_media_id"
              />
            </Box>
            <Button
              variant="contained"
              disabled={saving === 'wechat'}
              onClick={() =>
                runSave('wechat', async () => {
                  const updated = await systemApi.updateWechatConfig({
                    enabled: wxEnabled,
                    appId: wxAppId,
                    defaultCoverMediaId: wxCover,
                    ...(wxSecret.trim() ? { appSecret: wxSecret.trim() } : {}),
                  });
                  setWechat(updated);
                  setWxSecret('');
                  setSuccess(updated.ready ? '微信草稿同步已就绪' : '微信配置已保存');
                })
              }
            >
              {saving === 'wechat' ? <CircularProgress size={20} color="inherit" /> : '保存微信配置'}
            </Button>
          </Paper>

          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            以上配置保存在服务端 <code>data/runtime-settings.json</code>，保存即生效。多实例部署时仍建议用环境变量统一注入。
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SystemConfig;
